import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface PlaybackDaterangeData {
  title?: string;
  /** Earliest selectable instant (UTC). */
  minDate: string | Date;
  /** Latest selectable instant (UTC). */
  maxDate: string | Date;
  defaultFrom?: string | Date;
  defaultTo?: string | Date;
}

export interface PlaybackDaterangeResult {
  from: string; // ISO UTC
  to: string;   // ISO UTC
}

interface CalCell {
  day: number;        // day-of-month, 0 = padding
  date: Date | null;  // UTC midnight of the day
  locked: boolean;    // outside [min,max] range
  today: boolean;
  selected: boolean;
}

/**
 * Generic historical-playback date-range picker (design images 1 & 3).
 * Two From/To fields, each with a custom UTC calendar + 15-minute time column.
 * Days outside [minDate, maxDate] render locked with a padlock.
 */
@Component({
  selector: 'app-playback-daterange-modal',
  templateUrl: './playback-daterange-modal.component.html',
  styleUrls: ['./playback-daterange-modal.component.scss'],
})
export class PlaybackDaterangeModalComponent {
  title: string;
  min: Date;
  max: Date;

  from: Date;
  to: Date;

  activeField: 'from' | 'to' = 'from';
  /** First day of the currently displayed calendar month (UTC midnight). */
  visibleMonth: Date;
  weeks: CalCell[][] = [];
  times: string[] = [];   // 'HH:mm' every 15 min

  private readonly WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  readonly weekdays = this.WEEKDAYS;

  constructor(
    private dialogRef: MatDialogRef<PlaybackDaterangeModalComponent, PlaybackDaterangeResult>,
    @Inject(MAT_DIALOG_DATA) public data: PlaybackDaterangeData,
  ) {
    this.title = data.title ?? 'Historical Playback';
    this.min = new Date(data.minDate);
    this.max = new Date(data.maxDate);

    this.from = data.defaultFrom ? new Date(data.defaultFrom) : new Date(this.min);
    this.to   = data.defaultTo   ? new Date(data.defaultTo)   : new Date(this.max);

    this.times = this._buildTimes();
    this.visibleMonth = this._utcMonthStart(this.from);
    this._rebuild();
  }

  // ── Field state ──────────────────────────────────────────────────────────────

  get activeDate(): Date {
    return this.activeField === 'from' ? this.from : this.to;
  }

  focusField(field: 'from' | 'to'): void {
    this.activeField = field;
    this.visibleMonth = this._utcMonthStart(this.activeDate);
    this._rebuild();
  }

  // ── Calendar ─────────────────────────────────────────────────────────────────

  get monthLabel(): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[this.visibleMonth.getUTCMonth()]} ${this.visibleMonth.getUTCFullYear()}`;
  }

  prevMonth(): void { this._shiftMonth(-1); }
  nextMonth(): void { this._shiftMonth(1); }

  selectDay(cell: CalCell): void {
    if (!cell.date || cell.locked) return;
    const cur = this.activeDate;
    const next = new Date(Date.UTC(
      cell.date.getUTCFullYear(), cell.date.getUTCMonth(), cell.date.getUTCDate(),
      cur.getUTCHours(), cur.getUTCMinutes(), 0, 0,
    ));
    this._setActive(this._clamp(next));
    this._rebuild();
  }

  selectTime(hhmm: string): void {
    const [h, m] = hhmm.split(':').map(Number);
    const cur = this.activeDate;
    const next = new Date(Date.UTC(
      cur.getUTCFullYear(), cur.getUTCMonth(), cur.getUTCDate(), h, m, 0, 0,
    ));
    this._setActive(this._clamp(next));
    this._rebuild();
  }

  isActiveTime(hhmm: string): boolean {
    const cur = this.activeDate;
    const [h, m] = hhmm.split(':').map(Number);
    return cur.getUTCHours() === h && Math.floor(cur.getUTCMinutes() / 15) * 15 === m;
  }

  // ── Field display ────────────────────────────────────────────────────────────

  display(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let h = date.getUTCHours();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    const mm = String(date.getUTCMinutes()).padStart(2, '0');
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()} ${h}:${mm} ${ampm}`;
  }

  // ── Actions ──────────────────────────────────────────────────────────────────

  get invalid(): boolean {
    return this.from.getTime() >= this.to.getTime();
  }

  cancel(): void { this.dialogRef.close(); }

  load(): void {
    if (this.invalid) return;
    this.dialogRef.close({ from: this.from.toISOString(), to: this.to.toISOString() });
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  private _setActive(d: Date): void {
    if (this.activeField === 'from') this.from = d;
    else this.to = d;
  }

  private _shiftMonth(delta: number): void {
    this.visibleMonth = new Date(Date.UTC(
      this.visibleMonth.getUTCFullYear(), this.visibleMonth.getUTCMonth() + delta, 1,
    ));
    this._rebuild();
  }

  private _rebuild(): void {
    const y = this.visibleMonth.getUTCFullYear();
    const m = this.visibleMonth.getUTCMonth();
    const first = new Date(Date.UTC(y, m, 1));
    const startDow = first.getUTCDay();
    const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();

    const minDay = this._utcDayStart(this.min).getTime();
    const maxDay = this._utcDayStart(this.max).getTime();
    const todayDay = this._utcDayStart(new Date()).getTime();
    const selDay = this._utcDayStart(this.activeDate).getTime();

    const cells: CalCell[] = [];
    for (let i = 0; i < startDow; i++) {
      cells.push({ day: 0, date: null, locked: true, today: false, selected: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(Date.UTC(y, m, d));
      const t = date.getTime();
      cells.push({
        day: d,
        date,
        locked: t < minDay || t > maxDay,
        today: t === todayDay,
        selected: t === selDay,
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ day: 0, date: null, locked: true, today: false, selected: false });
    }

    this.weeks = [];
    for (let i = 0; i < cells.length; i += 7) this.weeks.push(cells.slice(i, i + 7));
  }

  private _clamp(d: Date): Date {
    const t = Math.max(this.min.getTime(), Math.min(this.max.getTime(), d.getTime()));
    return new Date(t);
  }

  private _buildTimes(): string[] {
    const out: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return out;
  }

  private _utcMonthStart(d: Date): Date {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  }

  private _utcDayStart(d: Date): Date {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  }
}
